// This module owns the always-visible, controller-agnostic top section of the
// MIDI Input settings panel: selected input port, mapping target, and controller
// geometry override. It does not render controller-
// specific controls or scale-mode behavior beyond the input-mode selector.

import PropTypes from "prop-types";
const GeneralInputSettings = ({
  hasBasicMidi,
  midi,
  settings,
  controller,
  controllerOverrideId,
  detectedController,
  controllerInfo,
  manualControllerOptions,
  linnstrumentUserFirmwareEligible,
  deactivateLinnstrumentUserFirmwareNow,
  resolveControllerSelection,
  isLinnstrumentUserFirmwareEligible,
  scaleMode,
  saveControllerPref,
  onChange,
}) => (
  <>
    <label>
      Input Port
      <select
        value={settings.midiin_device}
        name="midiin_device"
        class="sidebar-input"
        disabled={!hasBasicMidi}
        onChange={(e) => {
          if (linnstrumentUserFirmwareEligible && e.target.value !== settings.midiin_device) {
            deactivateLinnstrumentUserFirmwareNow();
          }
          onChange(e.target.name, e.target.value);
          sessionStorage.setItem(e.target.name, e.target.value);
        }}
      >
        <option value="OFF">OFF</option>
        {midi &&
          Array.from(midi.inputs.values()).map((m) => <option value={m.id}>{m.name}</option>)}
      </select>
    </label>

    <label>
      Controller Geometry
      <select
        class="sidebar-input"
        value={controllerOverrideId}
        disabled={!hasBasicMidi}
        onChange={(e) => {
          const nextCtrl = resolveControllerSelection(e.target.value, detectedController);
          const nextLinnstrumentUserFirmwareEligible = isLinnstrumentUserFirmwareEligible({
            controllerId: nextCtrl?.id ?? null,
            scaleMode,
            midiPassthrough: !!settings.midi_passthrough,
            midiinDevice: settings.midiin_device,
          });
          if (linnstrumentUserFirmwareEligible && !nextLinnstrumentUserFirmwareEligible) {
            deactivateLinnstrumentUserFirmwareNow();
          }
          onChange("midiin_controller_override", e.target.value);
          sessionStorage.setItem("midiin_controller_override", e.target.value);
        }}
      >
        <option value="auto">Auto Detect</option>
        {manualControllerOptions.map((option) => (
          <option value={option.id}>{option.label}</option>
        ))}
      </select>
    </label>

    {controllerInfo && (
      <label class="settings-form__description-label">
        {controllerInfo.name}
        <span class="sidebar-input settings-form__description-value">
          {controllerInfo.description}
        </span>
      </label>
    )}

    <label>
      Input Mode
      <select
        class="sidebar-input"
        value={settings.midiin_mapping_target || "hex_layout"}
        disabled={!hasBasicMidi}
        onChange={(e) => {
          if (linnstrumentUserFirmwareEligible && e.target.value !== "hex_layout") {
            deactivateLinnstrumentUserFirmwareNow();
          }
          onChange("midiin_mapping_target", e.target.value);
          if (controller) {
            saveControllerPref(controller, "midiin_mapping_target", e.target.value, settings, {
              midiin_mapping_target: e.target.value,
            });
          }
        }}
      >
        <option value="hex_layout">MIDI to Hex Layout</option>
        <option value="scale">MIDI to Nearest Scale Degree</option>
      </select>
    </label>
  </>
);

GeneralInputSettings.propTypes = {
  hasBasicMidi: PropTypes.bool.isRequired,
  midi: PropTypes.object,
  settings: PropTypes.object.isRequired,
  controller: PropTypes.object,
  controllerOverrideId: PropTypes.string.isRequired,
  detectedController: PropTypes.object,
  controllerInfo: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  manualControllerOptions: PropTypes.array.isRequired,
  linnstrumentUserFirmwareEligible: PropTypes.bool.isRequired,
  deactivateLinnstrumentUserFirmwareNow: PropTypes.func.isRequired,
  resolveControllerSelection: PropTypes.func.isRequired,
  isLinnstrumentUserFirmwareEligible: PropTypes.func.isRequired,
  scaleMode: PropTypes.bool.isRequired,
  saveControllerPref: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default GeneralInputSettings;
